import usePrefix from '@/hooks/usePrefix';
import React, { useState } from 'react';
import { IconButton } from 'rsuite';
import { Dropdown, Panel } from 'rsuite5';
import Image from './Image';
import PageLoader from '../shared/PageLoader';
import useRouter from '@/hooks/useRouter';
import { useReadyInfo } from '@/providers/ReadyProvider';
import { ROLE_TYPE, VILLAGE_TYPE } from '@/constants';
import { __PUBLIC__ } from '@/App';

const BicesterPanel = ({
  className,
  style,
  loading = false,
  withBorderTop = true,
  leftShadow = false,
  title,
  dividerHeight = 36,
  subTitle,
  toolbar,
  expanded = false,
  defaultExpand = true,
  onExpand,
  sideBarBtn = false,
  children,
  ...props
}: {
  className?: string;
  style?: any;
  loading?: any;
  withBorderTop?: boolean;
  leftShadow?: boolean;
  title?: React.ReactNode;
  dividerHeight?: number;
  subTitle?: React.ReactNode;
  toolbar?: React.ReactNode;
  expanded?: boolean; // 可展开
  defaultExpand?: boolean; // 默认展开
  onExpand?: (expanded: boolean) => void; // 点击展开按钮事件
  sideBarBtn?: boolean; // 侧边导航按钮
  children?: React.ReactNode;
  [rest: string]: any;
}) => {
  const { prefix, classname } = usePrefix('bicester-panel');
  const { match, pathname } = useRouter<{ id: string }>();
  const { profile } = useReadyInfo();

  const [isExpand, setIsExpand] = useState(defaultExpand);

  const hasHeader = !!(title || subTitle || toolbar || expanded);
  const headerContentAndMarginHeight = `calc(30px + ${dividerHeight}px)`;
  const bodyContentHeight = hasHeader ? `calc(100% - ${headerContentAndMarginHeight})` : '100%';

  const sideBarData = [
    {
      key: 'receipt',
      label: '上传小票',
      url: '/receipt/upload',
    },
    {
      key: 'lease',
      label: '新增租赁服务',
      url: '/site-service/lease',
    },
    {
      key: 'exchange',
      label: '新增兑换',
      url: '/exchange',
    },
    {
      key: 'replace-make',
      label: '代客预约服务',
      url: '/subscribe-service/replace-make',
    },
    {
      key: 'invoice',
      label: '发票登记',
      url: '/site-service/invoice',
    },
    {
      key: 'express',
      label: '商品快递登记',
      url: '/site-service/express',
    },
    {
      key: 'giftwrap',
      label: '礼品包装登记',
      url: '/site-service/giftwrap',
    },
    {
      key: 'third',
      label: '第三方积分登记',
      url: '/site-service/third',
    },
    {
      key: 'l-and-f',
      label: '失物&招领',
      url: '/site-service/l-and-f',
    },
    {
      key: 'suggest',
      label: '投诉与建议',
      url: '/site-service/suggest',
    },
    {
      key: 'luggage',
      label: '行李寄存登记',
      url: '/site-service/luggage',
    },
    {
      key: 'other',
      label: '其他服务登记',
      url: '/site-service/other',
    },
    {
      key: 'drawback',
      label: '退税登记',
      url: '/site-service/drawback',
    },
    {
      key: 'quick-parking',
      label: '优惠停车',
      url: '/customer-account/quick/parking',
    },
  ]
    .filter((bar) => {
      /**
       * 非会员的快捷按钮：
       * - 新增租赁服务
       * - 发票登记
       * - 商品快递登记
       * - 礼品包装登记
       * - 失物招领登记
       * - 投诉建议
       * - 行李寄存登记
       * - 其他服务登记
       */
      if (pathname.includes('no-member')) {
        return !(
          bar.key === 'receipt' ||
          bar.key === 'exchange' ||
          bar.key === 'replace-make' ||
          bar.key === 'quick-parking'
        );
      }
      /**
       * 会员的快捷按钮：
       * - 上传小票
       * - 新增租赁服务
       * - 新增兑换
       * - 代客预约服务
       * - 发票登记
       * - 商品快递登记
       * - 礼品包装登记
       * - 失物招领登记
       * - 投诉建议
       * - 行李寄存登记
       * - 其他服务登记
       * - 退税登记
       * - 优惠停车
       */
      return true;
    })
    .filter((bar) => {
      // 上海购物村的会员才有优惠停车
      if (profile?.shopping_village === VILLAGE_TYPE.SHANGHAI && !pathname.includes('no-member')) {
        return true;
      } else {
        return bar.key !== 'quick-parking';
      }
    })
    .filter((bar) => {
      if (profile?.role === ROLE_TYPE.AMBASSADOR) {
        // GE迎宾大使：没有优惠停车
        return bar.key !== 'quick-parking';
      }
      return true;
    })
    .filter((bar) => {
      // DRT导购大使：只有上传小票、优惠停车
      if (profile?.role === ROLE_TYPE.DRT) {
        return ['receipt', 'quick-parking'].includes(bar.key);
      }
      return true;
    });

  return (
    <Panel
      className={`${classname} px-[40px] py-[36px] ${
        withBorderTop ? 'border-t-[10px]' : 'border-t-0'
      } ${leftShadow ? 'left-shadow' : ''} ${className ?? ''}`}
      style={style}
      {...props}
    >
      {loading && <PageLoader />}
      {hasHeader && (
        <>
          <div className={prefix('header')}>
            <div className={prefix('left-wrap')}>
              <div className={prefix('title')}>{title}</div>
              {isExpand && <div className={prefix('sub-title')}>{subTitle}</div>}
              <div className={prefix('toolbar')}>{toolbar}</div>
            </div>
            {sideBarBtn && (
              <div className={prefix('sidebar')}>
                <Dropdown
                  renderToggle={(props) => (
                    <IconButton {...props} className="flex items-center">
                      <div className="text-[#455255] mr-[5px]">快捷服务</div>
                      <Image src="/resources/images/sidebar.svg" />
                    </IconButton>
                  )}
                  placement="leftStart"
                >
                  {sideBarData.map((item) => {
                    const { key, label, url } = item;
                    return (
                      <Dropdown.Item
                        icon={
                          <div className={prefix('sidebar-item-icon')}>
                            <Image src="/resources/images/sidebar-item-icon.svg" />
                          </div>
                        }
                        key={key}
                        onClick={() =>
                          window.open(
                            `${__PUBLIC__}${url}${
                              match.params.id
                                ? `?${pathname.includes('no-member') ? 'nomemberid' : 'memberid'}=${
                                    match.params.id
                                  }`
                                : ''
                            }`
                          )
                        }
                      >
                        <div className={prefix('sidebar-item-content')}>{label}</div>
                      </Dropdown.Item>
                    );
                  })}
                </Dropdown>
              </div>
            )}
            {expanded && (
              <div className={`${prefix('expand')} ${isExpand ? 'expanded' : ''}`}>
                <IconButton
                  onClick={() => {
                    setIsExpand(!isExpand);
                    onExpand && onExpand?.(!isExpand);
                  }}
                >
                  <Image className="w-full h-full" src="/resources/images/down-arrow.svg" />
                </IconButton>
              </div>
            )}
          </div>
          <div
            className="divider"
            style={{ ...(children && isExpand && { height: dividerHeight }) }}
          ></div>
        </>
      )}
      {((expanded && isExpand) || !expanded) && (
        <div className={`${prefix('body')}`} style={{ height: bodyContentHeight }}>
          {children}
        </div>
      )}
    </Panel>
  );
};

export default BicesterPanel;
